import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LogIn, LogOut, Loader2, ShieldAlert, Calendar, Mail,
  Settings as SettingsIcon, AlertCircle, Rocket, Image as ImageIcon,
  PartyPopper,
} from "lucide-react";
import BookingsTab from "@/components/admin/BookingsTab";
import ContactsTab from "@/components/admin/ContactsTab";
import SettingsTab from "@/components/admin/SettingsTab";
import EventsTab from "@/components/admin/EventsTab";
import DeployTab from "@/components/DeployTab";
import PhotosTab from "@/components/PhotosTab";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const TABS = [
  { id: "bookings", label: "Bookings", icon: Calendar, component: BookingsTab },
  { id: "contacts", label: "Messages", icon: Mail, component: ContactsTab },
  { id: "events", label: "Events", icon: PartyPopper, component: EventsTab },
  { id: "settings", label: "Site Settings", icon: SettingsIcon, component: SettingsTab },
  { id: "photos", label: "Photos", icon: ImageIcon, component: PhotosTab },
  { id: "deploy", label: "Deploy", icon: Rocket, component: DeployTab },
];

function LoginScreen({ authError, notAdmin }) {
  return (
    <div
      data-testid="admin-login"
      className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <div className="rounded-[2rem] border-2 border-leaf-200 bg-white p-8 shadow-[0_20px_60px_rgba(21,128,61,0.12)] sm:p-10 w-full">
        <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-leaf-100 text-leaf-700">
          <ShieldAlert className="h-7 w-7" />
        </div>
        <h1 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
          Shop Admin
        </h1>
        <p className="mt-3 text-leaf-900/70">
          Sign in with your shop Google account to manage bookings, contacts,
          and site content.
        </p>

        {authError && (
          <div className="mt-5 flex items-start gap-2 rounded-xl bg-clay-500/10 px-4 py-3 text-left text-sm text-clay-600">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <span>Sign-in didn't complete. Please try again.</span>
          </div>
        )}

        {notAdmin && (
          <div className="mt-5 flex items-start gap-2 rounded-xl bg-sun-100 px-4 py-3 text-left text-sm text-leaf-900">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-clay-500" />
            <span>
              You're signed in, but this Google account isn't on the shop
              allowlist. Contact the shop owner.
            </span>
          </div>
        )}

        <button
          data-testid="admin-google-login"
          onClick={startGoogleLogin}
          className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-leaf-600 px-6 py-3.5 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
        >
          <LogIn className="h-5 w-5" /> Sign in with Google
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("bookings");
  const params = new URLSearchParams(window.location.search);
  const authError = params.get("auth_error") === "1";

  if (loading || user === null) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-leaf-700">
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
    );
  }

  if (!user) return <LoginScreen authError={authError} />;
  if (!user.is_admin) return <LoginScreen notAdmin />;

  const ActiveTab = TABS.find((t) => t.id === tab)?.component || BookingsTab;

  return (
    <div data-testid="page-admin" className="px-4 pt-10 sm:px-8 md:px-12">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-700">
              Admin
            </p>
            <h1 className="mt-1 font-display text-4xl font-black text-leaf-900 sm:text-5xl">
              Shop dashboard
            </h1>
            <p className="mt-2 text-leaf-900/70">
              Signed in as <span className="font-semibold">{user.email}</span>
            </p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border-2 border-leaf-900 px-5 py-2.5 text-sm font-bold text-leaf-900 hover:bg-leaf-900 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>

        <nav className="mt-8 flex flex-wrap gap-2 border-b-2 border-leaf-100 pb-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                tab === t.id
                  ? "bg-leaf-600 text-white shadow"
                  : "text-leaf-900 hover:bg-leaf-50"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </nav>

        <div className="mt-8">
          <ActiveTab />
        </div>
      </div>
    </div>
  );
}
